import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, helpers } from '../../utils';
import { toast } from "react-toastify";
import _ from 'lodash';

//creating context
const SpecificationContextState = createContext();
const SpecificationContextUpdater = createContext();
const initialSelect = { label: "--- Select ---", value: "--- Select ---" };

const SpecificationContextProvider = ({ children }) => {
   //value to be given to context
   const [specification, setSpecification] = useState(null);
   const [selectedSpecification, setSelectedSpecification] = useState(initialSelect);
   const [needleSizeOptions, setNeedleSizeOptions] = useState(null);

   const firstRender = useRef(true);
   const tenacityGroup = ['strengthMin', 'strengthNom'];// do one update
   const sharedTenacityAndYieldGroup = ['yarnSizeUnitOfMeasure', 'yarnSizeNom', 'yarnSizeMin', 'yarnSizeMax', 'strengthMin', 'strengthNom'];// do both updates

   useEffect(() => {
      api
         .getNeedleSizes()
         .then((response) => {
            if (response.success) {
               setNeedleSizeOptions(helpers.addLabelValueToList(response.needleSizes));
            } else {
               console.info("needle size list came back null")
            }
         })
         .catch(api.catchHandler);
   }, []);

   useEffect(() => {
      // Set the spec to null every time so it clears all entry field values when loading a new item.
      // We had instances where old values were lingering from the last spec when those same fields
      // were null on the newly retrieved spec and fields continued to display the old values. This
      // is dangerous as those old lingering values may get saved by mistake causing pricing issues.
      // If the fetch below works it will repopulate the entry fields with correct values for the spec.
      setSpecification(null);

      if (selectedSpecification === initialSelect) {
         if (firstRender.current) {
            firstRender.current = false;
            return;
         } else {
            toast.info("Please Select an item to display your specification results");
            return;
         }
      }
      if (selectedSpecification === null) {
         return;
      }
      const fetchSpecification = () => {
         api.fetch(`Quality/GetSpecificationBySpec/${selectedSpecification.value}`)
            .then(r => {
               if (r.data?.success) {
                  let spec = r.data.spec;
                  setSpecification(spec);
               } else {
                  toast.error(`This Specification does not exist`);
               }
            })
            .catch(api.catchHandler);
      }
      fetchSpecification();
   }, [selectedSpecification]);

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const GetDenierYarnSizeFromCottonYarnSize = useCallback((val) => {
      if (typeof val != "number") return new Error("passed a value to GetDenierYarnSizeFromCottonYarnSize that was not a number")
      let num = -2147483648;
      if (val === 0) {
         return num;
      }
      num = val;
      return ((590.541247448236 * 9) / num);
   });

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChange = useCallback((propertyName, value) => {
      //onUpdateTenacity
      if (_.includes(sharedTenacityAndYieldGroup, propertyName)) {
         let copy = _.cloneDeep(specification);
         _.set(copy, propertyName, value);
         const { yarnSizeUnitOfMeasure, strengthMin, strengthNom, yarnSizeNom, yarnSizeMin, yarnSizeMax } = copy;

         let NotCnstYarnSizeNom = 0;
         let NotCnstYarnSizeMin = 0;
         let NotCnstYarnSizeMax = 0;
         if (yarnSizeUnitOfMeasure === "Cotton Count") {
            NotCnstYarnSizeNom = _.isNil(yarnSizeNom) ? 0 : GetDenierYarnSizeFromCottonYarnSize(yarnSizeNom);
            NotCnstYarnSizeMin = _.isNil(yarnSizeMin) ? 0 : GetDenierYarnSizeFromCottonYarnSize(yarnSizeMin);
            NotCnstYarnSizeMax = _.isNil(yarnSizeMax) ? 0 : GetDenierYarnSizeFromCottonYarnSize(yarnSizeMax);
         } else { // Min & Max are reversed on denier
            NotCnstYarnSizeNom = _.isNil(yarnSizeNom) ? 0 : yarnSizeNom;
            NotCnstYarnSizeMin = _.isNil(yarnSizeMax) ? 0 : yarnSizeMax;
            NotCnstYarnSizeMax = _.isNil(yarnSizeMin) ? 0 : yarnSizeMin;
         }

         let tenacityNom = 0;
         let tenacityMin = 0;
         if (!_.includes(tenacityGroup, propertyName)) {
            // this is a yield group update, update both yield and tenacity values
            let yieldNom = 0;
            let yieldMin = 0;
            let yieldMax = 0;
            if (NotCnstYarnSizeNom !== 0) {
               yieldNom = (4464500 / NotCnstYarnSizeNom)
               tenacityNom = ((strengthNom * 453.59238) / NotCnstYarnSizeNom)
            }
            if (NotCnstYarnSizeMin !== 0) {
               yieldMin = (4464500 / NotCnstYarnSizeMin)
               tenacityMin = ((strengthMin * 453.59237) / NotCnstYarnSizeMin)
            }
            if (NotCnstYarnSizeMax !== 0) {
               yieldMax = (4464500 / NotCnstYarnSizeMax)
            }
            setSpecification({
               ...specification,
               [propertyName]: value,
               yieldNom: yieldNom,
               yieldMin: yieldMin,
               yieldMax: yieldMax,
               tenacityNom: tenacityNom,
               tenacityMin: tenacityMin
            })
         } else {
            // this is a tenacity group update, only update tenacity
            if (NotCnstYarnSizeNom !== 0) {
               tenacityNom = ((strengthNom * 453.59238) / NotCnstYarnSizeNom)
            }
            if (NotCnstYarnSizeMin !== 0) {
               tenacityMin = ((strengthMin * 453.59237) / NotCnstYarnSizeMin)
            }
            setSpecification({
               ...specification,
               [propertyName]: value,
               tenacityNom: tenacityNom,
               tenacityMin: tenacityMin
            })
         }
      } else if (propertyName === "calFirstTwist" || propertyName === "calSecondTwist") {
         if (!value && propertyName === "calFirstTwist") {
            setSpecification({
               ...specification,
               [propertyName]: value,
               firstTwistDirection: ""
            })
            return;
         }
         if (!value && propertyName === "calSecondTwist") {
            setSpecification({
               ...specification,
               [propertyName]: value,
               secondTwistDirection: ""
            })
            return;
         }
         setSpecification({
            ...specification,
            [propertyName]: value
         })
      } else {
         console.log("value", value);
         setSpecification({
            ...specification,
            [propertyName]: value
         })
      }
   })

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onSelectChange = useCallback((specNumber) => {
      if (specNumber === null) {
         setSelectedSpecification(null);
         return;
      }
      if (typeof specNumber?.value !== 'string' || specNumber?.value.length <= 1)
         toast.error(`${specNumber} must be a string and length must be greater than one`);
      setSelectedSpecification(specNumber)
   })

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const refreshSpecs = useCallback(() => {
      // setting this to the same thing but because it is an object it will trigger the useEffect
      setSelectedSpecification({
         label: selectedSpecification.label,
         value: selectedSpecification.value
      })
   });

   // memoize the full context value
   const contextValue = useMemo(() => ({
      specification,
      selectedSpecification,
      needleSizeOptions
   }), [specification, selectedSpecification, needleSizeOptions])

   const contextUpdater = useMemo(() => ({
      onChange,
      onSelectChange,
      refreshSpecs
   }), [onChange, onSelectChange, refreshSpecs])

   return (
      //the provide gives access to the context to its children
      <SpecificationContextState.Provider value={contextValue}>
         <SpecificationContextUpdater.Provider value={contextUpdater}>
            {children}
         </SpecificationContextUpdater.Provider>
      </SpecificationContextState.Provider>
   );
};

export { SpecificationContextState, SpecificationContextUpdater, SpecificationContextProvider };
